import React, { useState, useEffect } from 'react';
import {
  Box, Typography, List, ListItemButton, Chip,
  useMediaQuery, useTheme, Collapse, Tooltip,
} from '@mui/material';
import { ExpandMore, ExpandLess, Print, ListAltOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../layout/PublicLayout';

/* ─── Types ──────────────────────────────────────────────────────────────── */
export interface TOCEntry  { id: string; label: string }
export interface PolicyBadge { label: string; color: string; bg: string }

interface LayoutProps {
  title: string;
  lastUpdated: string;
  effectiveDate?: string;
  badges?: PolicyBadge[];
  toc: TOCEntry[];
  related?: Array<{ label: string; href: string }>;
  children: React.ReactNode;
}

/* ─── Shared body components (exported for pages) ───────────────────────── */

export const PolicySection: React.FC<{
  id: string; number?: string; title: string; children: React.ReactNode;
}> = ({ id, number, title, children }) => (
  <Box
    id={id}
    sx={{
      mb: 0, scrollMarginTop: '96px', pb: 5, pt: 0.5,
      borderBottom: '1px solid #f3f4f6',
      '&:last-of-type': { borderBottom: 'none', pb: 0 },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5, mt: 5 }}>
      {number && (
        <Typography sx={{
          fontSize: '0.7rem', fontWeight: 700, color: '#d1d5db',
          letterSpacing: '0.04em', mt: 1, flexShrink: 0, fontVariantNumeric: 'tabular-nums',
        }}>
          {number}
        </Typography>
      )}
      <Typography sx={{
        fontSize: { xs: '1.1rem', md: '1.2rem' }, fontWeight: 700,
        color: '#111827', letterSpacing: '-0.015em', lineHeight: 1.3,
      }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

export const Para: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{
    fontSize: '0.925rem', color: '#374151', lineHeight: 1.9,
    mb: 1.75, '&:last-child': { mb: 0 },
  }}>
    {children}
  </Typography>
);

export const BulletList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="ul"
    sx={{
      pl: 0, m: 0, mb: 2, listStyle: 'none',
      '& > li': {
        fontSize: '0.925rem', color: '#374151', lineHeight: 1.9,
        mb: 0.875, display: 'flex', alignItems: 'flex-start', gap: 1.5,
      },
    }}
  >
    {children}
  </Box>
);

export const BulletItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="li">
    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#dc2626', flexShrink: 0, mt: '11px' }} />
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

export const SubHead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#1f2937', mt: 2.5, mb: 1 }}>
    {children}
  </Typography>
);

export const InfoBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ bgcolor: '#eff6ff', borderLeft: '3px solid #3b82f6', borderRadius: '0 8px 8px 0', p: 2.5, mb: 2.5 }}>
    <Typography sx={{ fontSize: '0.875rem', color: '#1e40af', lineHeight: 1.8 }}>{children}</Typography>
  </Box>
);

export const WarnBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ bgcolor: '#fff7ed', borderLeft: '3px solid #f97316', borderRadius: '0 8px 8px 0', p: 2.5, mb: 2.5 }}>
    <Typography sx={{ fontSize: '0.875rem', color: '#9a3412', lineHeight: 1.8 }}>{children}</Typography>
  </Box>
);

export const ContactCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ bgcolor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', p: 3, mt: 2.5 }}>
    <Typography sx={{ fontSize: '0.875rem', color: '#374151', lineHeight: 2.3 }}>{children}</Typography>
  </Box>
);

/* ─── Layout ─────────────────────────────────────────────────────────────── */

const PolicyPageLayout: React.FC<LayoutProps> = ({
  title, lastUpdated, effectiveDate, badges, toc, related, children,
}) => {
  const [activeId, setActiveId] = useState<string>(toc[0]?.id ?? '');
  const [tocOpen, setTocOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-88px 0px -55% 0px', threshold: 0 },
    );
    toc.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [toc]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 94, behavior: 'smooth' });
    setTocOpen(false);
  };

  return (
    <PublicLayout>

      {/* ── Breadcrumb ── */}
      <Box sx={{ pt: { xs: '76px', md: '80px' }, bgcolor: '#fff', borderBottom: '1px solid #f3f4f6' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: 5 }, py: 1.3, display: 'flex', alignItems: 'center' }}>
          {[{ label: 'Home', href: '/' }, { label: 'Legal', href: null }, { label: title, href: null }].map((c, i) => (
            <React.Fragment key={i}>
              {i > 0 && <Typography sx={{ fontSize: '0.72rem', color: '#d1d5db', mx: 0.75 }}>›</Typography>}
              {c.href
                ? <Typography onClick={() => navigate(c.href!)} sx={{ fontSize: '0.72rem', color: '#9ca3af', cursor: 'pointer', '&:hover': { color: '#dc2626' }, transition: 'color 0.15s' }}>{c.label}</Typography>
                : <Typography sx={{ fontSize: '0.72rem', color: i === 2 ? '#4b5563' : '#9ca3af', fontWeight: i === 2 ? 500 : 400 }}>{c.label}</Typography>
              }
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {/* ── Header ── */}
      <Box sx={{ bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: 5 }, pt: { xs: 6, md: 8 }, pb: { xs: 5, md: 6 } }}>
          <Typography sx={{
            fontSize: { xs: '1.9rem', md: '2.625rem' }, fontWeight: 800,
            color: '#111827', mb: 2, letterSpacing: '-0.035em', lineHeight: 1.15,
          }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: badges?.length ? 2.5 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#22c55e', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.82rem', color: '#6b7280' }}>
                Last updated:&nbsp;<Box component="span" sx={{ color: '#1f2937', fontWeight: 600 }}>{lastUpdated}</Box>
              </Typography>
            </Box>
            {effectiveDate && (
              <>
                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: '#d1d5db' }} />
                <Typography sx={{ fontSize: '0.82rem', color: '#6b7280' }}>
                  Effective:&nbsp;<Box component="span" sx={{ color: '#1f2937', fontWeight: 600 }}>{effectiveDate}</Box>
                </Typography>
              </>
            )}
            <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: '#d1d5db' }} />
            <Tooltip title="Print or save as PDF" placement="right">
              <Box
                onClick={() => window.print()}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: '#9ca3af', '&:hover': { color: '#dc2626' }, userSelect: 'none', transition: 'color 0.15s' }}
              >
                <Print sx={{ fontSize: '0.95rem' }} />
                <Typography sx={{ fontSize: '0.82rem' }}>Print / PDF</Typography>
              </Box>
            </Tooltip>
          </Box>
          {badges?.length ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {badges.map(b => (
                <Chip key={b.label} label={b.label} size="small" sx={{ bgcolor: b.bg, color: b.color, fontWeight: 600, fontSize: '0.72rem', height: 26, borderRadius: '6px' }} />
              ))}
            </Box>
          ) : null}
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ bgcolor: '#fff', width: '100%' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 0, md: 5 }, py: { xs: 5, md: 6 }, display: 'flex', alignItems: 'flex-start', gap: { md: 6 }, width: '100%', boxSizing: 'border-box' }}>

          {/* Desktop sidebar */}
          {!isMobile && (
            <Box sx={{
              width: 210, flexShrink: 0, position: 'sticky', top: '88px',
              maxHeight: 'calc(100vh - 108px)', overflowY: 'auto', pr: 1,
              '&::-webkit-scrollbar': { width: 3 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#e5e7eb', borderRadius: 4 },
            }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#9ca3af', letterSpacing: '0.14em', textTransform: 'uppercase', mb: 1.5, px: 1 }}>
                On this page
              </Typography>
              <List disablePadding>
                {toc.map(item => (
                  <ListItemButton
                    key={item.id}
                    onClick={() => scrollTo(item.id)}
                    sx={{
                      py: 0.65, px: 1, borderRadius: '6px', mb: 0.15,
                      borderLeft: `2px solid ${activeId === item.id ? '#dc2626' : 'transparent'}`,
                      bgcolor: activeId === item.id ? '#fef2f2' : 'transparent',
                      '&:hover': { bgcolor: activeId === item.id ? '#fef2f2' : '#f9fafb' },
                    }}
                  >
                    <Typography sx={{
                      fontSize: '0.775rem', lineHeight: 1.45,
                      color: activeId === item.id ? '#dc2626' : '#6b7280',
                      fontWeight: activeId === item.id ? 600 : 400,
                      transition: 'color 0.15s',
                    }}>
                      {item.label}
                    </Typography>
                  </ListItemButton>
                ))}
              </List>
            </Box>
          )}

          {/* Main content */}
          <Box sx={{ flex: 1, minWidth: 0, px: { xs: 3, md: 0 }, overflow: 'hidden' }}>

            {/* Mobile TOC */}
            {isMobile && (
              <Box sx={{ mb: 4 }}>
                <Box
                  onClick={() => setTocOpen(!tocOpen)}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, border: '1px solid #e5e7eb', borderRadius: 2, cursor: 'pointer', bgcolor: '#f9fafb' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <ListAltOutlined sx={{ fontSize: '1rem', color: '#9ca3af' }} />
                    <Typography sx={{ fontSize: '0.83rem', fontWeight: 600, color: '#374151' }}>Jump to section</Typography>
                  </Box>
                  {tocOpen
                    ? <ExpandLess sx={{ color: '#9ca3af', fontSize: '1.1rem' }} />
                    : <ExpandMore sx={{ color: '#9ca3af', fontSize: '1.1rem' }} />
                  }
                </Box>
                <Collapse in={tocOpen}>
                  <Box sx={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                    {toc.map((item, i) => (
                      <Box
                        key={item.id}
                        onClick={() => scrollTo(item.id)}
                        sx={{ px: 2.5, py: 1.15, cursor: 'pointer', borderBottom: i < toc.length - 1 ? '1px solid #f3f4f6' : 'none', bgcolor: activeId === item.id ? '#fef2f2' : '#fff', '&:hover': { bgcolor: '#f9fafb' } }}
                      >
                        <Typography sx={{ fontSize: '0.81rem', color: activeId === item.id ? '#dc2626' : '#4b5563', fontWeight: activeId === item.id ? 600 : 400 }}>
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            )}

            {children}

            {/* Related legal docs footer */}
            {related && related.length > 0 && (
              <Box sx={{ mt: 10, pt: 5, borderTop: '2px solid #f3f4f6' }}>
                <Typography sx={{ fontSize: '0.63rem', fontWeight: 800, color: '#9ca3af', letterSpacing: '0.14em', textTransform: 'uppercase', mb: 2.5 }}>
                  Related Legal Documents
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {related.map(p => (
                    <Box
                      key={p.href}
                      component="a"
                      href={p.href}
                      onClick={(e: React.MouseEvent) => { e.preventDefault(); navigate(p.href); }}
                      sx={{ fontSize: '0.82rem', color: '#374151', textDecoration: 'none', px: 2.5, py: 0.875, border: '1px solid #e5e7eb', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', '&:hover': { borderColor: '#fca5a5', color: '#dc2626', bgcolor: '#fef2f2' }, transition: 'all 0.15s' }}
                    >
                      {p.label}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </PublicLayout>
  );
};

export default PolicyPageLayout;
